import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Sheet,
  Typography,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Checkbox,
  IconButton,
  Chip
} from '@mui/joy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import SettingsIcon from '@mui/icons-material/Settings';
import { get, post, put, del, patch } from '../utils/ApiClient';

/**
 * VorhaltepauschaleManager Component
 * Manages configurable criteria for Vorhaltepauschale calculation
 */
const VorhaltepauschaleManager = () => {
  const [kriterien, setKriterien] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingKriterium, setEditingKriterium] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    gop_codes: '',
    threshold_q1: 0,
    threshold_q2: 0,
    threshold_q3: 0,
    threshold_q4: 0,
    active: true,
    sort_order: 0
  });
  const [baseGopCodes, setBaseGopCodes] = useState('');
  const [configModalOpen, setConfigModalOpen] = useState(false);

  // Load criteria on mount
  useEffect(() => {
    loadKriterien();
    loadConfig();
  }, []);

  const loadKriterien = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await get('/api/vorhaltepauschale-kriterien');
      setKriterien(data);
    } catch (err) {
      console.error('Error loading kriterien:', err);
      setError('Fehler beim Laden der Kriterien');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const config = await get('/api/vorhaltepauschale-config');
      if (config && config.base_gop_codes) {
        // Convert JSON array to comma-separated string
        const parsed = JSON.parse(config.base_gop_codes);
        setBaseGopCodes(Array.isArray(parsed) ? parsed.join(', ') : config.base_gop_codes);
      }
    } catch (err) {
      console.error('Error loading config:', err);
    }
  };

  const handleSaveConfig = async () => {
    try {
      // Convert comma-separated to JSON array
      const gopCodesArray = baseGopCodes
        .split(',')
        .map(code => code.trim())
        .filter(code => code.length > 0);

      await put('/api/vorhaltepauschale-config', {
        base_gop_codes: JSON.stringify(gopCodesArray)
      });

      setConfigModalOpen(false);
      alert('Konfiguration erfolgreich gespeichert');
    } catch (err) {
      console.error('Error saving config:', err);
      alert('Fehler beim Speichern der Konfiguration');
    }
  };

  const handleAdd = () => {
    setEditingKriterium(null);
    setFormData({
      name: '',
      description: '',
      gop_codes: '',
      threshold_q1: 0,
      threshold_q2: 0,
      threshold_q3: 0,
      threshold_q4: 0,
      active: true,
      sort_order: kriterien.length
    });
    setModalOpen(true);
  };

  const handleEdit = (kriterium) => {
    setEditingKriterium(kriterium);

    // Convert JSON array to comma-separated string for editing
    let gopCodesStr = '';
    try {
      if (kriterium.gop_codes) {
        const parsed = JSON.parse(kriterium.gop_codes);
        if (Array.isArray(parsed)) {
          gopCodesStr = parsed.join(', ');
        } else {
          gopCodesStr = kriterium.gop_codes;
        }
      }
    } catch (e) {
      gopCodesStr = kriterium.gop_codes || '';
    }

    setFormData({
      name: kriterium.name,
      description: kriterium.description || '',
      gop_codes: gopCodesStr,
      threshold_q1: kriterium.threshold_q1,
      threshold_q2: kriterium.threshold_q2,
      threshold_q3: kriterium.threshold_q3,
      threshold_q4: kriterium.threshold_q4,
      active: kriterium.active === 1,
      sort_order: kriterium.sort_order
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie dieses Kriterium wirklich löschen?')) {
      return;
    }

    try {
      await del(`/api/vorhaltepauschale-kriterien/${id}`);
      await loadKriterien();
    } catch (err) {
      console.error('Error deleting kriterium:', err);
      alert('Fehler beim Löschen des Kriteriums');
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await patch(`/api/vorhaltepauschale-kriterien/${id}/toggle`);
      await loadKriterien();
    } catch (err) {
      console.error('Error toggling kriterium:', err);
      alert('Fehler beim Aktivieren/Deaktivieren des Kriteriums');
    }
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.name) {
        alert('Bitte geben Sie einen Namen ein');
        return;
      }

      // Convert comma-separated GOP codes to JSON array
      let gopCodesJson;
      try {
        // Split by comma, trim whitespace, filter empty strings
        const gopCodesArray = formData.gop_codes
          .split(',')
          .map(code => code.trim())
          .filter(code => code.length > 0);

        gopCodesJson = JSON.stringify(gopCodesArray);
      } catch (e) {
        alert('Fehler beim Verarbeiten der GOP-Codes. Bitte überprüfen Sie die Eingabe.');
        return;
      }

      const payload = {
        ...formData,
        gop_codes: gopCodesJson,
        active: formData.active ? 1 : 0
      };

      if (editingKriterium) {
        // Update existing
        await put(`/api/vorhaltepauschale-kriterien/${editingKriterium.id}`, payload);
      } else {
        // Create new
        await post('/api/vorhaltepauschale-kriterien', payload);
      }

      setModalOpen(false);
      await loadKriterien();
    } catch (err) {
      console.error('Error saving kriterium:', err);
      alert('Fehler beim Speichern des Kriteriums');
    }
  };

  const parseGopCodes = (gopCodesStr) => {
    try {
      if (!gopCodesStr) return [];
      if (typeof gopCodesStr === 'string') {
        const parsed = JSON.parse(gopCodesStr);
        return Array.isArray(parsed) ? parsed : [];
      }
      return [];
    } catch (e) {
      return [];
    }
  };

  if (loading) {
    return <Typography>Laden...</Typography>;
  }

  if (error) {
    return <Typography color="danger">{error}</Typography>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography level="h4">Vorhaltepauschale Kriterien</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<SettingsIcon />}
            onClick={() => setConfigModalOpen(true)}
          >
            Basis-GOPs konfigurieren
          </Button>
          <Button startDecorator={<AddIcon />} onClick={handleAdd}>
            Kriterium hinzufügen
          </Button>
        </Stack>
      </Stack>

      <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
        <Table>
          <thead>
            <tr>
              <th style={{ width: '5%' }}>ID</th>
              <th style={{ width: '20%' }}>Name</th>
              <th style={{ width: '25%' }}>GOP-Codes</th>
              <th style={{ width: '10%' }}>Q1 (%)</th>
              <th style={{ width: '10%' }}>Q2 (%)</th>
              <th style={{ width: '10%' }}>Q3 (%)</th>
              <th style={{ width: '10%' }}>Q4 (%)</th>
              <th style={{ width: '5%' }}>Status</th>
              <th style={{ width: '5%' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {kriterien.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                  Keine Kriterien vorhanden
                </td>
              </tr>
            ) : (
              kriterien.map((kriterium) => (
                <tr key={kriterium.id}>
                  <td>{kriterium.id}</td>
                  <td>
                    <Typography level="body-sm">{kriterium.name}</Typography>
                  </td>
                  <td>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {parseGopCodes(kriterium.gop_codes).slice(0, 5).map((code, idx) => (
                        <Chip key={idx} size="sm" variant="soft">
                          {code}
                        </Chip>
                      ))}
                      {parseGopCodes(kriterium.gop_codes).length > 5 && (
                        <Chip size="sm" variant="soft">
                          +{parseGopCodes(kriterium.gop_codes).length - 5}
                        </Chip>
                      )}
                    </Box>
                  </td>
                  <td>{kriterium.threshold_q1}%</td>
                  <td>{kriterium.threshold_q2}%</td>
                  <td>{kriterium.threshold_q3}%</td>
                  <td>{kriterium.threshold_q4}%</td>
                  <td>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color={kriterium.active === 1 ? 'success' : 'neutral'}
                      onClick={() => handleToggleActive(kriterium.id)}
                    >
                      {kriterium.active === 1 ? <ToggleOnIcon /> : <ToggleOffIcon />}
                    </IconButton>
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="primary"
                        onClick={() => handleEdit(kriterium)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleDelete(kriterium.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Sheet>

      {/* Edit/Add Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <ModalDialog sx={{ minWidth: 600, maxWidth: 800 }}>
          <ModalClose />
          <Typography level="h4">
            {editingKriterium ? 'Kriterium bearbeiten' : 'Neues Kriterium'}
          </Typography>

          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl required>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Haus- und Pflegeheimbesuche"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Beschreibung</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optionale Beschreibung"
                minRows={2}
              />
            </FormControl>

            <FormControl required>
              <FormLabel>GOP-Codes</FormLabel>
              <Textarea
                value={formData.gop_codes}
                onChange={(e) => setFormData({ ...formData, gop_codes: e.target.value })}
                placeholder='01410, 01411, 01412, 01413'
                minRows={3}
              />
              <Typography level="body-xs" color="neutral">
                Geben Sie die GOP-Codes mit Komma getrennt ein, z.B.: 01410, 01411, 01412
              </Typography>
            </FormControl>

            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Schwelle Q1 (%)</FormLabel>
                <Input
                  type="number"
                  value={formData.threshold_q1}
                  onChange={(e) =>
                    setFormData({ ...formData, threshold_q1: parseFloat(e.target.value) || 0 })
                  }
                  slotProps={{ input: { min: 0, max: 100, step: 0.1 } }}
                />
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Schwelle Q2 (%)</FormLabel>
                <Input
                  type="number"
                  value={formData.threshold_q2}
                  onChange={(e) =>
                    setFormData({ ...formData, threshold_q2: parseFloat(e.target.value) || 0 })
                  }
                  slotProps={{ input: { min: 0, max: 100, step: 0.1 } }}
                />
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Schwelle Q3 (%)</FormLabel>
                <Input
                  type="number"
                  value={formData.threshold_q3}
                  onChange={(e) =>
                    setFormData({ ...formData, threshold_q3: parseFloat(e.target.value) || 0 })
                  }
                  slotProps={{ input: { min: 0, max: 100, step: 0.1 } }}
                />
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Schwelle Q4 (%)</FormLabel>
                <Input
                  type="number"
                  value={formData.threshold_q4}
                  onChange={(e) =>
                    setFormData({ ...formData, threshold_q4: parseFloat(e.target.value) || 0 })
                  }
                  slotProps={{ input: { min: 0, max: 100, step: 0.1 } }}
                />
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Sortierreihenfolge</FormLabel>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) =>
                    setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })
                  }
                  slotProps={{ input: { min: 0 } }}
                />
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Status</FormLabel>
                <Checkbox
                  label="Aktiv"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="outlined" color="neutral" onClick={() => setModalOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSave}>Speichern</Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Config Modal */}
      <Modal open={configModalOpen} onClose={() => setConfigModalOpen(false)}>
        <ModalDialog sx={{ minWidth: 500 }}>
          <ModalClose />
          <Typography level="h4">
            Basis-GOP-Codes für Behandlungsfälle
          </Typography>

          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography level="body-sm" color="neutral">
              Diese GOP-Codes definieren, welche Patienten als "Behandlungsfälle" gezählt werden.
              Nur Patienten mit mindestens einem dieser Codes werden im Nenner der Vorhaltepauschale berücksichtigt.
            </Typography>

            <FormControl required>
              <FormLabel>Basis-GOP-Codes</FormLabel>
              <Textarea
                value={baseGopCodes}
                onChange={(e) => setBaseGopCodes(e.target.value)}
                placeholder='03001, 03002, 03003, 03004, 03005'
                minRows={3}
              />
              <Typography level="body-xs" color="neutral">
                Geben Sie die GOP-Codes mit Komma getrennt ein, z.B.: 03001, 03002, 03003, 03004, 03005
              </Typography>
            </FormControl>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="outlined" color="neutral" onClick={() => setConfigModalOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSaveConfig}>Speichern</Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default VorhaltepauschaleManager;
